import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  TextField,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ClearIcon from "@mui/icons-material/Clear";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import PreviewIcon from "@mui/icons-material/Preview";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import AssetsManagerZoom from "./AssetsManagerZoom";
import { colorStyling } from "../../../helpers/color";
import {
  TypographyLarge,
  TypographyNormal,
  TypographySmall,
  TypographyXSmall,
} from "../../customComponent";
import { splitFileName } from "../../../helpers/fileProcessing";
import parser from "html-react-parser";
import { sanitizeInput } from "../../../helpers/inputProcessing";

const StyledBox = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  ":last-child": {
    marginBottom: 0,
  },
}));

const AssetsManagerViewAsset = ({
  asset,
  assetType,
  updatePermission,
  handleUpdate,
}) => {
  const { loadingUpdate, assetsTags } = useSelector((state) => state.asset);

  const { t } = useTranslation("assetmanager");
  const { t: tCommon } = useTranslation();

  const [open, setOpen] = useState(false);
  const [viewAsset, setViewAsset] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCampaignName, setNewCampaignName] = useState("");
  const [editName, setEditName] = useState(false);
  const [editCampaignName, setEditCampaignName] = useState(false);
  const [currentlyEditing, setCurrentlyEditing] = useState(false);

  const [assetTags, setAssetTags] = useState({});
  const [tagInput, setTagInput] = useState("");
  const [tagValidation, setTagValidation] = useState("");

  const [nameValidation, setNameValidation] = useState("");
  const [campaignNameValidation, setCampaignNameValidation] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }

    const whichTags = assetsTags[asset.id] || asset.tags;
    const existingTags = {};
    if (whichTags?.length > 0) {
      whichTags.forEach((tag) => (existingTags[tag] = true));
    }
    setAssetTags(existingTags);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const assetName = useMemo(() => {
    const split = splitFileName(asset.name);
    return split;
  }, [asset.name]);

  const handleCallback = (event) => {
    event.preventDefault();

    let notValidFlag;
    const payload = { id: asset.id };
    if (editName && newName.length > 0) {
      if (newName.trim() === "") {
        setNameValidation(tCommon("validation.fieldMustNotEmpty"));
        setNewName("");
        notValidFlag = true;
      }
      payload["name"] = `${newName.trim()}.${assetName.extension}`;
      payload["name"] = sanitizeInput(payload.name);
    }
    if (editCampaignName && newCampaignName.length > 0) {
      if (newCampaignName.trim() === "") {
        setCampaignNameValidation(tCommon("validation.fieldMustNotEmpty"));
        setNewCampaignName("");
        notValidFlag = true;
      }
      payload["campaignName"] = newCampaignName.trim();
      payload["campaignName"] = sanitizeInput(payload.campaignName);
    }

    const tagsPayload = [];
    Object.keys(assetTags).forEach((tag) => tagsPayload.push(tag));
    payload["tags"] = tagsPayload;

    if (notValidFlag) return;
    handleUpdate(payload, handleClose);
  };

  const handleOpen = async () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setViewAsset(false);
    setNewName("");
    setNewCampaignName("");
    setEditName(false);
    setEditCampaignName(false);
    setCurrentlyEditing(false);
    setNameValidation("");
    setCampaignNameValidation("");
  };

  const handleChangeName = (event) => {
    const value = event.target.value;
    setNewName(value);
    setNameValidation("");
    if (value === "" && !editCampaignName) {
      setCurrentlyEditing(false);
    } else {
      setCurrentlyEditing(true);
    }
  };

  const handleChangeCampaignName = (event) => {
    const value = event.target.value;
    setNewCampaignName(value);
    setCampaignNameValidation("");
    if (value === "" && !editName) {
      setCurrentlyEditing(false);
    } else {
      setCurrentlyEditing(true);
    }
  };

  const handleEditToggle = (which, value) => {
    let nameFlag;
    switch (which) {
      case "name":
        nameFlag = true;
        break;

      case "campaignName":
        nameFlag = false;
        break;

      default:
        break;
    }

    if (!value) {
      if (nameFlag) {
        if (newCampaignName.length === 0) {
          setCurrentlyEditing(false);
        }
      } else {
        if (newName.length === 0) {
          setCurrentlyEditing(false);
        }
      }
    } else {
      if (nameFlag) {
        if (newName !== assetName.name && newName.length > 0) {
          setCurrentlyEditing(true);
        }
      } else {
        if (
          newCampaignName !== (asset.campaignName ?? "") &&
          newCampaignName.length > 0
        ) {
          setCurrentlyEditing(true);
        }
      }
    }

    if (nameFlag) {
      setEditName(value);
      if (value === false) {
        setNameValidation("");
      }
    } else {
      setEditCampaignName(value);
      if (value === false) {
        setCampaignNameValidation("");
      }
    }
  };

  const tags = useMemo(() => {
    const parsedTags = [];
    Object.keys(assetTags).forEach((tag) => parsedTags.push(tag));
    return parsedTags;
  }, [assetTags]);

  const handleTagInputChange = (event) => {
    const input = event.target.value;
    // max input to 20 characters
    if (input?.length > 20) {
      setTagValidation(t("asset.tagInputReachedMax"));
      const textAfter = input.slice(0, 20);
      setTagInput(textAfter);
      return;
    }
    setTagValidation("");
    setTagInput(input);
  };

  const handleTagInput = (event) => {
    if (event.key === "Enter") {
      const tag = tagInput.trim();
      if (tag === "") {
        setTagInput("");
        return;
      }
      if (assetTags[tag]) {
        setTagValidation(t("asset.tagErrorAlreadyExist"));
        return;
      }
      if (/[^a-zA-Z0-9]/.test(tag)) {
        setTagValidation(t("asset.tagErrorInvalid"));
        return;
      }

      const updatedTags = { ...assetTags, [tag]: true };
      setAssetTags(updatedTags);
      setTagValidation("");
      setTagInput("");
    }
  };

  const handleTagRemove = (tag) => {
    const updatedTags = { ...assetTags };
    delete updatedTags[tag];
    setAssetTags(updatedTags);
  };

  const tagsChanged = () => {
    const whichTags = assetsTags[asset.id] || asset.tags;
    if (!whichTags || !tags || whichTags.length !== tags.length) {
      return true;
    }

    const set1 = new Set(whichTags);
    const set2 = new Set(tags);

    for (const item of set1) {
      if (!set2.has(item)) {
        return true;
      }
    }

    return false;
  };

  return (
    <>
      <Tooltip
        arrow
        disableInteractive
        title={
          <TypographySmall sx={{ color: "white", textAlign: "center" }}>
            {t("asset.viewBtn")}
          </TypographySmall>
        }
      >
        <Box>
          <IconButton
            variant="contained"
            disabled={!(assetType.isImage || assetType.isVideo)}
            onClick={() => {
              handleOpen();
            }}
            sx={{
              color: colorStyling.primary,
            }}
          >
            <PreviewIcon />
          </IconButton>
        </Box>
      </Tooltip>

      <Dialog
        maxWidth="md"
        fullWidth
        open={open}
        onClose={() => (!loadingUpdate ? handleClose() : null)}
      >
        <Box>
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Tooltip
              title={assetName.name.length > 20 ? assetName.name : ""}
              placement="top-end"
            >
              <Box>
                {`${t("asset.previewAssetPrefix")} [${
                  assetName.name?.length > 20
                    ? assetName.name.substring(0, 19) +
                      `...${assetName.extension}`
                    : asset.name
                }]`}
              </Box>
            </Tooltip>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            <AssetsManagerZoom
              open={viewAsset}
              closeDialog={() => setViewAsset(false)}
              asset={asset}
              assetType={assetType}
            />

            <Box display={"flex"} sx={{ px: 1, py: 2 }}>
              <Box
                display={"flex"}
                flexDirection={"column"}
                justifyContent={"center"}
              >
                <Link
                  href="#"
                  underline="none"
                  component={"div"}
                  onClick={() => setViewAsset(true)}
                  sx={{
                    width: "333px",
                    position: "relative",
                    cursor: "pointer",
                    ":hover": {
                      opacity: 0.9,
                    },
                  }}
                >
                  {asset && (
                    <img
                      alt={asset.name}
                      style={{
                        width: "100%",
                        height: "222px",
                      }}
                      src={asset?.thumbnailUrl ?? asset?.url}
                    />
                  )}
                  {assetType.isVideo && (
                    <PlayCircleIcon
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        color: colorStyling.white.full,
                        fontSize: "77px",
                      }}
                    />
                  )}
                </Link>
                <Box display={"flex"} justifyContent={"center"}>
                  <Box
                    display={"flex"}
                    alignItems={"center"}
                    onClick={() => setViewAsset(true)}
                    sx={{
                      ":hover": {
                        cursor: "pointer",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    <ZoomInIcon fontSize="15px" sx={{ mt: "4px" }} />
                    <TypographySmall
                      width={"fit-content"}
                      textAlign={"center"}
                      sx={{ pt: "4px" }}
                    >
                      {t("asset.zoomHint")}
                    </TypographySmall>
                  </Box>
                </Box>
              </Box>

              <Box
                display={"flex"}
                flexDirection={"column"}
                justifyContent={"space-between"}
                sx={{ pl: 2 }}
              >
                <Box display={"flex"} flexDirection={"column"}>
                  <DialogContentText
                    sx={{
                      mb: 1,
                      textAlign: "center",
                      fontWeight: 600,
                      textDecoration: "underline",
                    }}
                  >
                    {t("asset.assetDetailsLabel")}
                  </DialogContentText>

                  <Box
                    sx={{
                      p: 2,
                    }}
                  >
                    <Box
                      display={"flex"}
                      flexDirection={"column"}
                      sx={{
                        width: "333px",
                      }}
                    >
                      <StyledBox>
                        <InputLabel>{t("asset.fileNameFullLabel")}</InputLabel>

                        <TextField
                          required
                          margin="dense"
                          fullWidth
                          size="small"
                          variant="outlined"
                          placeholder={t("asset.newFileNamePlaceholder")}
                          value={editName ? newName : assetName.name}
                          disabled={!editName || loadingUpdate}
                          onChange={(e) =>
                            editName ? handleChangeName(e) : null
                          }
                          InputProps={{
                            endAdornment: (
                              <InputAdornment
                                position="end"
                                sx={{ ml: "10px", fontWeight: 600 }}
                              >
                                .{assetName.extension}
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor: colorStyling.black,
                              backgroundColor: colorStyling.white.inactive,
                            },
                          }}
                        />
                        <TypographySmall sx={{ color: colorStyling.red }}>
                          {nameValidation}
                        </TypographySmall>

                        {updatePermission && !loadingUpdate && (
                          <Link
                            href="#"
                            underline="none"
                            component={"div"}
                            onClick={() => handleEditToggle("name", !editName)}
                            sx={{
                              ml: 1,
                              cursor: "pointer",
                            }}
                          >
                            <TypographySmall
                              sx={{
                                fontWeight: 600,
                                color: colorStyling.blue.light,
                              }}
                            >
                              {!editName
                                ? t("asset.clickEditBtn")
                                : t("asset.cancelEditBtn")}
                            </TypographySmall>
                          </Link>
                        )}
                      </StyledBox>

                      <StyledBox>
                        <InputLabel>{t("asset.campaignNameLabel")}</InputLabel>

                        <TextField
                          required
                          margin="dense"
                          fullWidth
                          size="small"
                          variant="outlined"
                          placeholder={t("asset.newCampaignNamePlaceholder")}
                          value={
                            editCampaignName
                              ? newCampaignName
                              : asset.campaignName ?? "—"
                          }
                          disabled={!editCampaignName || loadingUpdate}
                          onChange={(e) =>
                            editCampaignName
                              ? handleChangeCampaignName(e)
                              : null
                          }
                          sx={{
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor: colorStyling.black,
                              backgroundColor: colorStyling.white.inactive,
                            },
                          }}
                        />
                        <TypographySmall sx={{ color: colorStyling.red }}>
                          {campaignNameValidation}
                        </TypographySmall>

                        {updatePermission && !loadingUpdate && (
                          <Link
                            href="#"
                            underline="none"
                            component={"div"}
                            onClick={() =>
                              handleEditToggle(
                                "campaignName",
                                !editCampaignName
                              )
                            }
                            sx={{
                              ml: 1,
                              cursor: "pointer",
                            }}
                          >
                            <TypographySmall
                              sx={{
                                fontWeight: 600,
                                color: colorStyling.blue.light,
                              }}
                            >
                              {!editCampaignName
                                ? t("asset.clickEditBtn")
                                : t("asset.cancelEditBtn")}
                            </TypographySmall>
                          </Link>
                        )}
                      </StyledBox>

                      <StyledBox>
                        <Box
                          display={"flex"}
                          flexDirection={"column"}
                          sx={{
                            mt: 1,
                            height: "max-content",
                            placeSelf: "start",
                          }}
                        >
                          <InputLabel>{t("asset.assetTagsLabel")}</InputLabel>
                          <TypographyXSmall>
                            {parser(t("asset.assetTagsHint"))}
                          </TypographyXSmall>
                        </Box>

                        <Box display={"flex"} flexDirection={"column"}>
                          <TextField
                            margin="dense"
                            fullWidth
                            size="small"
                            variant="outlined"
                            autoComplete="off"
                            placeholder={t("asset.tagPlaceholder")}
                            value={tagInput}
                            onChange={handleTagInputChange}
                            onKeyDown={handleTagInput}
                            sx={{
                              "& .MuiInputBase-input.Mui-disabled": {
                                WebkitTextFillColor: colorStyling.black,
                                backgroundColor: colorStyling.white.inactive,
                              },
                            }}
                          />

                          <TypographySmall
                            sx={{
                              ml: 2,
                              color: colorStyling.red,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {tagValidation}
                          </TypographySmall>

                          <Box
                            display={"flex"}
                            gap={1}
                            sx={{ mt: 1, ml: 2, flexWrap: "wrap" }}
                          >
                            {tags.map((item, index) => (
                              <Chip
                                key={index}
                                label={item}
                                size="small"
                                color="success"
                                onDelete={() => handleTagRemove(item)}
                                sx={{
                                  color: "white",
                                  backgroundColor: "mediumseagreen",
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      </StyledBox>

                      <StyledBox display={"flex"} alignItems={"baseline"}>
                        <InputLabel>{t("asset.fileSizeLabel")}</InputLabel>
                        <TypographyLarge sx={{ ml: 2 }}>
                          {(asset.size / (1024 * 1024)).toFixed(2)} MB
                        </TypographyLarge>
                      </StyledBox>

                      {asset.canDelete ? (
                        <TypographyNormal textAlign={"center"} sx={{ mt: 2 }}>
                          {parser(t("asset.inUseHint"))}
                        </TypographyNormal>
                      ) : (
                        <TypographyNormal textAlign={"center"} sx={{ mt: 2 }}>
                          {parser(t("asset.notUsedHint"))}
                        </TypographyNormal>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </DialogContent>

          <DialogActions>
            <Button
              startIcon={<ClearIcon />}
              fullWidth
              variant="outlined"
              disabled={loadingUpdate}
              onClick={handleClose}
              sx={{ height: "38px" }}
            >
              {tCommon("closeBtn")}
            </Button>

            {(editName || editCampaignName || tagsChanged()) && (
              <Button
                type="submit"
                startIcon={!loadingUpdate && <DoneIcon />}
                fullWidth
                variant="contained"
                onClick={handleCallback}
                disabled={
                  loadingUpdate || (!currentlyEditing && !tagsChanged())
                }
                sx={{ height: "38px" }}
              >
                {!loadingUpdate ? (
                  tCommon("submitBtn")
                ) : (
                  <CircularProgress size={"22px"} color="inherit" />
                )}
              </Button>
            )}
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
};

export default AssetsManagerViewAsset;
